import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Ban,
  CheckCircle2,
  Download,
  FileCog,
  Inbox,
  MessageSquarePlus,
  Pencil,
  Send,
  Trash2,
  Upload,
  Wallet,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import DOMPurify from "dompurify";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { EmptyState, PageHeader, SectionCard } from "../components/ui";
import { MzPoaFeeModal } from "../components/mz/MzPoaFeeModal";
import { MzRequestFeeModal } from "../components/mz/MzRequestFeeModal";
import { EditDooModal } from "../components/DooFeeAttach";
import { OrderHistorySection } from "../components/orders/OrderHistorySection";
import { OrderOrdererControl } from "../components/orders/OrderOrdererControl";
import { OrderRevertControl } from "../components/orders/OrderRevertControl";
import { MzStatusBadge } from "../components/mz/MzStatusBadge";
import { MzTimeline } from "../components/mz/MzTimeline";
import { useMdcpBuffers, useMzOrder, useMzOrderMutations, useTaskPolling } from "../hooks";
import { fmtDate, fmtDateTime } from "../utils";
import type { ManagedFileMini } from "../types";
import { MZ_ORDER_STATUS_LABELS, WYPIS_APPLICANT_LABELS } from "../types";

const FEE_STATUS_LABELS: Record<string, string> = {
  pending_approval: "Oczekuje na akceptację",
  approved: "Zaakceptowana — do opłacenia",
  rejected: "Odrzucona",
  payment_uploaded: "Opłacona",
  acknowledged: "Opłacona i potwierdzona",
  withdrawn: "Wycofana",
};

const CLOSED = ["sent", "received", "completed", "cancelled"];

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3", "a"],
    ALLOWED_ATTR: ["start", "href", "target", "rel", "class"],
  });
}

function FileLink({ file, label }: { file: ManagedFileMini | null; label: string }) {
  if (!file?.url) return null;
  return (
    <a
      href={file.url}
      download={file.original_filename}
      className="inline-flex items-center gap-1 text-xs font-mono border border-[var(--color-border-strong)] px-2 py-1 hover:bg-[var(--color-bg-elevated)]"
    >
      <Download size={12} /> {label}
    </a>
  );
}

export function MzOrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const { data: order, isLoading } = useMzOrder(orderId);
  const m = useMzOrderMutations(orderId);
  const { data: internalUsers } = useInternalUsers();
  // Obrysy projektu — zakres wniosku bierze się z jednego z nich (panel MDCP projektu).
  const { data: buffersData } = useMdcpBuffers(order?.connection);
  const projectBuffers = buffersData?.buffers ?? [];
  const contactOptions = useMemo(
    () => (internalUsers ?? []).map((u) => ({ value: String(u.id), label: u.full_name || u.username })),
    [internalUsers],
  );

  const [generateTaskId, setGenerateTaskId] = useState<string | null>(null);
  const generateTask = useTaskPolling(generateTaskId);
  const [feeModal, setFeeModal] = useState(false);
  const [editDooModal, setEditDooModal] = useState(false);
  const [poaFeeModal, setPoaFeeModal] = useState(false);
  // Osobne pola na wektor i na licencję — patrz sekcja „Pliki ze starostwa".
  const [mapUploads, setMapUploads] = useState<File[]>([]);
  const [licenceUploads, setLicenceUploads] = useState<File[]>([]);
  const [otherUploads, setOtherUploads] = useState<File[]>([]);
  // Pytanie „zamknąć bez pliku mapy?" — backend odmawia miękko, decyzja należy do człowieka.
  const [completeAsk, setCompleteAsk] = useState("");
  const [commentText, setCommentText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState("");
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [actionError, setActionError] = useState("");

  // A finished generation task means the order rows changed — refetch the files.
  useEffect(() => {
    const done = (s?: string) => s && ["success", "failure", "cancelled"].includes(s);
    if (generateTaskId && done(generateTask.data?.status)) {
      qc.invalidateQueries({ queryKey: ["pp", "mz-order", orderId] });
    }
  }, [generateTask.data?.status, generateTaskId, orderId, qc]);

  // Lustro bramki backendu: autor, jego następca (osoba do kontaktów po nieaktywnym
  // autorze), dyrektor, CEO — nie zgadujemy tego z `created_by`.
  const canManage = !!order?.can_manage;

  if (isLoading || !order) {
    return <div className="py-16 flex justify-center"><Spinner /></div>;
  }

  const generating = !!generateTaskId && !["success", "failure", "cancelled"].includes(generateTask.data?.status ?? "");

  const runAction = (fn: () => void) => { setActionError(""); fn(); };
  const onActionError = (e: unknown) => {
    const detail =
      (e as { response?: { data?: { errors?: { detail?: unknown } } } })?.response?.data?.errors
        ?.detail;
    setActionError(typeof detail === "string" ? detail : "Operacja nie powiodła się.");
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {order.number} <MzStatusBadge status={order.status} />
          </span>
        }
        subtitle={
          <>
            <Link to={`/power-projects/${order.project_id}`} className="hover:underline">
              {order.project_name}
            </Link>
            {` · powiat ${order.county || "—"} · ${order.starostwo_name || "brak adresata"} · skala ${order.scale} · EPSG:${order.srid} · wnioskodawca: ${WYPIS_APPLICANT_LABELS[order.applicant] ?? "ELEKTIM"}`}
          </>
        }
        backTo="/power-projects/zamawianie?tab=mz"
        backLabel="Mapy zasadnicze"
        actions={
          canManage && order.status === "new" ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => runAction(() => m.cancel.mutate(order.id, { onError: onActionError }))}
            >
              <Ban size={13} className="mr-1" /> Anuluj zamówienie
            </Button>
          ) : undefined
        }
      />

      <MzTimeline order={order} />
      <OrderOrdererControl
        ordererId={order.created_by}
        ordererName={order.created_by_name}
        ordererPhone={order.created_by_phone}
        ordererEmail={order.created_by_email}
        takenOverByName={order.taken_over_by_name}
        takenOverByPhone={order.taken_over_by_phone}
        takenOverByEmail={order.taken_over_by_email}
        canManage={canManage}
        pending={m.changeOrderer.isPending}
        onChange={(userId) =>
          runAction(() =>
            m.changeOrderer.mutate({ id: order.id, userId }, { onError: onActionError }),
          )
        }
      />
      {/* Cofanie etapów — cel wyznacza backend; przy „zakończonym" to jedyna droga
          z powrotem, bo status zakończony jest terminalny w biegu naprzód. */}
      {canManage && (
        <OrderRevertControl
          status={order.status}
          revertTo={order.revert_to}
          revertLabel={order.revert_label}
          statusLabels={MZ_ORDER_STATUS_LABELS}
          pending={m.revert.isPending}
          onRevert={(reason) =>
            runAction(() =>
              m.revert.mutate({ id: order.id, reason }, { onError: onActionError }),
            )
          }
        />
      )}
      {actionError && <div className="text-xs text-red-600">{actionError}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          {/* ── dokumenty ── */}
          <SectionCard
            title="Dokumenty wniosku"
            action={
              !CLOSED.includes(order.status) ? (
                <Button
                  size="sm"
                  loading={generating || m.generate.isPending}
                  onClick={() =>
                    runAction(() =>
                      m.generate.mutate(order.id, {
                        onSuccess: (r) => setGenerateTaskId(r.task_id),
                        onError: onActionError,
                      }),
                    )
                  }
                >
                  <FileCog size={13} className="mr-1" />
                  {order.has_documents ? "Generuj ponownie" : "Generuj dokumenty"}
                </Button>
              ) : undefined
            }
          >
            <div className="mb-3 text-xs text-[var(--color-text-secondary)]">
              {/* Który obrys — projekt ma ich wiele, a wniosek niesie ten wskazany przy
                  zakładaniu zamówienia (przełączenie obrysu na mapie go nie rusza). */}
              Zakres = obrys MDCP projektu{order.buffer_name ? <> („<b>{order.buffer_name}</b>”)</> : null}
              {order.clipped_to_county ? (
                <>
                  {" "}przycięty do granicy powiatu <b>{order.county || order.county_teryt}</b>{" "}
                  (z zapasem 0,5 m) — pozostałe powiaty trasy mają własne wnioski
                </>
              ) : null}
              {" "}— wniosek (formularz P) prosi o wektor .dxf w skali{" "}
              <b>{order.scale}</b>, w układzie <b>EPSG:{order.srid}</b>; obrys idzie jako załącznik DXF
              z układem wpisanym w rysunek.{" "}
              <Link to={`/power-projects/${order.project_id}`} className="text-[var(--color-primary)] hover:underline">
                Obrys edytujesz w panelu MDCP projektu →
              </Link>
            </div>
            {/* Podmiana obrysu, dopóki wniosek nie poszedł do starostwa — zły wybór w
                modalu da się naprawić bez zakładania zamówienia od nowa. Zmiana unieważnia
                wygenerowany komplet (obrys JEST zakresem wniosku i załącznika DXF). */}
            {!CLOSED.includes(order.status) && projectBuffers.length > 1 && (
              <label className="block mb-3 max-w-sm">
                <span className="section-label">Obrys (zakres wniosku)</span>
                <select
                  aria-label="Obrys wniosku MZ"
                  className="input-base w-full"
                  value={order.buffer ?? ""}
                  onChange={(e) => {
                    if (
                      order.has_documents &&
                      !window.confirm(
                        "Zmiana obrysu unieważni wygenerowany wniosek i załącznik DXF. Kontynuować?",
                      )
                    ) return;
                    m.update.mutate({ id: order.id, payload: { buffer: e.target.value } });
                  }}
                >
                  {order.buffer == null && <option value="">— bez wskazania —</option>}
                  {projectBuffers.map((b) => (
                    <option key={b.id} value={b.id}>
                      {[
                        b.display_name,
                        b.area_m2 != null ? `${(b.area_m2 / 10000).toFixed(2)} ha` : "brak obrysu",
                        b.is_active ? "wybrany na mapie" : null,
                      ].filter(Boolean).join(" · ")}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!CLOSED.includes(order.status) && (
              <label className="block mb-3 max-w-sm">
                <span className="section-label">Osoba do kontaktu (poz. 4 i 9 formularza)</span>
                <SearchableSelect
                  options={contactOptions}
                  value={order.contact_person ?? (me ? String(me.id) : "")}
                  placeholder="Wybierz osobę…"
                  onChange={(e) =>
                    m.update.mutate({ id: order.id, payload: { contact_person: e.target.value } })
                  }
                />
                {order.has_documents && (
                  <span className="text-[11px] text-[var(--color-text-muted)]">
                    Po zmianie wygeneruj dokumenty ponownie.
                  </span>
                )}
              </label>
            )}
            {generating ? (
              <div className="flex items-center gap-2 text-xs py-2">
                <Spinner size="sm" /> Generuję dokumenty…
              </div>
            ) : order.has_documents ? (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <FileLink file={order.form_file} label="Wniosek — formularz P (PDF)" />
                  <FileLink file={order.attachment_dxf} label="Załącznik — obrys (DXF)" />
                </div>
                <div className="text-[11px] text-[var(--color-text-muted)]">
                  Wygenerowano {fmtDateTime(order.generated_at)} — sprawdź wizualnie wypełnienie
                  formularza przed wysyłką.
                </div>
              </div>
            ) : (
              <EmptyState>
                {generateTask.data?.status === "failure"
                  ? "Generacja nie powiodła się — sprawdź, czy projekt ma obrys MDCP, i spróbuj ponownie."
                  : "Brak wygenerowanych dokumentów"}
              </EmptyState>
            )}
          </SectionCard>

          {/* ── opłata za pełnomocnictwo ── */}
          <SectionCard
            title="Opłata za pełnomocnictwo (17 zł)"
            action={
              canManage && !CLOSED.includes(order.status) ? (
                <Button size="sm" variant="secondary" onClick={() => setPoaFeeModal(true)}>
                  <Wallet size={13} className="mr-1" />
                  {order.poa_fees.length > 0
                    ? "Dodaj kolejną"
                    : order.poa_fee_not_required
                      ? "Zmień"
                      : "Rozlicz"}
                </Button>
              ) : undefined
            }
          >
            {order.poa_fee_not_required ? (
              <div className="text-xs text-[var(--color-text-secondary)]">
                Oznaczona jako niepotrzebna
                {order.poa_fee_not_required_reason ? ` — ${order.poa_fee_not_required_reason}` : ""}.
              </div>
            ) : order.poa_fees.length > 0 ? (
              <div className="space-y-2 text-xs">
                {order.poa_fees.map((poaFee) => (
                  <div key={poaFee.id} className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge
                        color={
                          poaFee.status === "payment_uploaded" || poaFee.status === "acknowledged"
                            ? "green"
                            : poaFee.status === "rejected" || poaFee.status === "withdrawn"
                              ? "red"
                              : "yellow"
                        }
                      >
                        {FEE_STATUS_LABELS[poaFee.status] ?? poaFee.status}
                      </Badge>
                      <span className="font-mono">{poaFee.amount} PLN</span>
                      <span className="text-[var(--color-text-secondary)]">{poaFee.office_name}</span>
                      {poaFee.paid_at && (
                        <span className="text-[var(--color-text-secondary)]">
                          opłacono {fmtDate(poaFee.paid_at)}
                        </span>
                      )}
                      <Link to={`/official-fees/${poaFee.id}`} className="hover:underline text-[var(--color-primary)]">
                        otwórz w /official-fees →
                      </Link>
                      {poaFee.payment_files.map((f) =>
                        f.url ? (
                          <a
                            key={f.id}
                            href={f.url}
                            download={f.original_filename}
                            title={`Pobierz: ${f.original_filename}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
                          >
                            <Download size={12} /> PDF
                          </a>
                        ) : null
                      )}
                      {canManage && !CLOSED.includes(order.status) && (
                        <button
                          className="ml-auto text-[var(--color-text-muted)] hover:text-red-600"
                          title="Odepnij opłatę od zamówienia (opłata pozostaje w /official-fees)"
                          onClick={() =>
                            runAction(() =>
                              m.setPoaFee.mutate(
                                { id: order.id, payload: { mode: "detach", fee: poaFee.id } },
                                { onError: onActionError },
                              ),
                            )
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                    {poaFee.rejection_comment && (
                      <div className="text-red-600">Powód odrzucenia: {poaFee.rejection_comment}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState>
                Wymagana domyślnie (ELEKTIM jako pełnomocnik inwestora) — podepnij/poproś o
                opłatę albo oznacz jako niepotrzebną przed wysyłką.
              </EmptyState>
            )}
          </SectionCard>

          {/* ── opłata ── */}
          <SectionCard
            title="Opłata urzędowa (DOO)"
            action={
              // „sent" tak samo jak „new" — DOO przychodzi ze starostwa dopiero po wniosku.
              canManage && order.status !== "cancelled" ? (
                <div className="flex items-center gap-2">
                  {(order.fee_id || order.doo_file) && (
                    <Button size="sm" variant="secondary" onClick={() => setEditDooModal(true)}>
                      Edytuj DOO
                    </Button>
                  )}
                  {["new", "sent"].includes(order.status) && (
                    <Button size="sm" onClick={() => setFeeModal(true)}>
                      <Wallet size={13} className="mr-1" /> {order.fee_id ? "Złóż ponownie" : "Dodaj opłatę"}
                    </Button>
                  )}
                </div>
              ) : undefined
            }
          >
            {order.fee_id ? (
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <Badge
                    color={
                      order.fee_status === "payment_uploaded" || order.fee_status === "acknowledged"
                        ? "green"
                        : order.fee_status === "rejected" || order.fee_status === "withdrawn"
                          ? "red"
                          : "yellow"
                    }
                  >
                    {FEE_STATUS_LABELS[order.fee_status ?? ""] ?? order.fee_status}
                  </Badge>
                  {order.fee_amount && <span className="font-mono">{order.fee_amount} PLN</span>}
                  <Link to={`/official-fees/${order.fee_id}`} className="hover:underline text-[var(--color-primary)]">
                    otwórz w /official-fees →
                  </Link>
                  {order.fee_payment_files?.map((f) =>
                    f.url ? (
                      <a
                        key={f.id}
                        href={f.url}
                        download={f.original_filename}
                        title={`Pobierz: ${f.original_filename}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium"
                      >
                        <Download size={12} /> PDF
                      </a>
                    ) : null
                  )}
                </div>
                {order.fee_rejection_comment && (
                  <div className="text-red-600">Powód odrzucenia: {order.fee_rejection_comment}</div>
                )}
                {/* Prośby w toku nie odpinamy tutaj — wisi jako żywa pozycja w
                    /official-fees i to tam się ją wycofuje (zamówienie wróci samo). */}
                {order.status === "fee_requested" && (
                  <div className="text-[var(--color-text-muted)]">
                    Prośba o opłatę jest w toku — żeby ją cofnąć, wycofaj opłatę w
                    /official-fees; zamówienie wróci wtedy do statusu „Nowe”.
                  </div>
                )}
              </div>
            ) : order.status === "sent" ? (
              <EmptyState>
                Gdy starostwo przyśle DOO, kliknij „Dodaj opłatę” i wgraj tam PDF — kwota, konto
                i tytuł przelewu uzupełnią się automatycznie.
              </EmptyState>
            ) : (
              <EmptyState>Opłata opcjonalna — można też od razu oznaczyć wysyłkę (DOO przyjdzie po wniosku).</EmptyState>
            )}
            {order.doo_file?.url && (
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-[var(--color-text-secondary)]">DOO ze starostwa:</span>
                <a
                  href={order.doo_file.url}
                  download={order.doo_file.original_filename}
                  title={`Pobierz: ${order.doo_file.original_filename}`}
                  className="inline-flex items-center gap-1 hover:underline text-[var(--color-primary)]"
                >
                  <Download size={12} /> {order.doo_file.original_filename}
                </a>
              </div>
            )}
          </SectionCard>
        </div>

        <div className="space-y-4">
          {/* ── wysyłka / odbiór ── */}
          <SectionCard title="Wysyłka i odbiór">
            <div className="space-y-2 text-xs">
              {["new", "fee_paid"].includes(order.status) && canManage && (
                <Button
                  size="sm"
                  onClick={() =>
                    runAction(() => m.markSent.mutate({ id: order.id }, { onError: onActionError }))
                  }
                  loading={m.markSent.isPending}
                >
                  <Send size={13} className="mr-1" /> Oznacz: wysłano do starostwa
                </Button>
              )}
              {order.sent_at && (
                <div>
                  Wysłano {fmtDateTime(order.sent_at)} ({order.sent_by_name})
                </div>
              )}
              {order.status === "sent" && canManage && (
                <Button
                  size="sm"
                  onClick={() =>
                    runAction(() => m.markReceived.mutate(order.id, { onError: onActionError }))
                  }
                  loading={m.markReceived.isPending}
                >
                  <Inbox size={13} className="mr-1" /> Oznacz: mapa przyszła
                </Button>
              )}
              {order.received_at && (
                <div>
                  Otrzymano {fmtDateTime(order.received_at)} ({order.received_by_name})
                </div>
              )}
              {order.status === "received" && canManage && (
                <div className="space-y-1">
                  <Button
                    size="sm"
                    onClick={() =>
                      runAction(() =>
                        m.complete.mutate(
                          { id: order.id },
                          {
                            onError: (e) => {
                              // Brak pliku mapy to PYTANIE, nie ściana: mapa bywa
                              // dostarczona inną drogą, a zamówienie i tak trzeba zamknąć.
                              const errors = (e as {
                                response?: { data?: { errors?: { detail?: string; code?: string } } };
                              })?.response?.data?.errors;
                              if (errors?.code === "no_map_file" && errors.detail) {
                                setCompleteAsk(errors.detail);
                                return;
                              }
                              onActionError(e);
                            },
                          },
                        ),
                      )
                    }
                    loading={m.complete.isPending}
                  >
                    <CheckCircle2 size={13} className="mr-1" /> Zakończ zamówienie
                  </Button>
                  {completeAsk && (
                    <div className="border border-amber-300 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-2 space-y-1.5 text-xs">
                      <div className="text-amber-700 dark:text-amber-400">{completeAsk}</div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          loading={m.complete.isPending}
                          onClick={() =>
                            runAction(() =>
                              m.complete.mutate(
                                { id: order.id, confirmWithoutFiles: true },
                                { onSuccess: () => setCompleteAsk(""), onError: onActionError },
                              ),
                            )
                          }
                        >
                          Zamknij mimo to
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setCompleteAsk("")}>
                          Anuluj
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {order.status === "new" && !order.fee_id && (
                <div className="text-[var(--color-text-muted)]">
                  Wysyłka możliwa bez opłaty albo po jej opłaceniu.
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── pliki ze starostwa ── */}
          <SectionCard title={`Pliki ze starostwa (${order.result_files.length})`}>
            <div className="space-y-2">
              {/* Także na zamówieniu zakończonym — starostwo bywa, że dosyła dokument
                  osobną przesyłką po zamknięciu sprawy. Status zostaje „Zakończone". */}
              {["sent", "received", "completed"].includes(order.status) && canManage && (
                <div className="space-y-3">
                  {/* Osobne pola: mapa wektorowa i licencja to różne pliki o różnym
                      dalszym życiu (wektor → rysunek projektanta, licencja → załącznik
                      wniosków urzędowych). Jedno pole z przełącznikiem kazało pamiętać,
                      co jest ustawione — a pomyłka podpinała licencję jako mapę.
                      „Inne pliki" (dowolny format, wiele naraz) lądują jako OTHER —
                      poza wektorem projektanta i poza licencją wniosków. */}
                  {([
                    ["map", "Mapa wektorowa", ".dxf,.dwg,.gml,.zip,.rar,.7z,.tif,.tiff",
                     "DXF / DWG / GML / ZIP — plik dla projektanta"],
                    ["licencja", "Licencja mapy zasadniczej", ".pdf,.xml,.odt",
                     "PDF / XML / ODT — podepnie się sam do wniosków; z XML powstaje w tle automatyczna wizualizacja PDF, z ODT konwersja do PDF"],
                    ["other", "Inne pliki", undefined,
                     "dowolny format, wiele naraz — korespondencja, wezwania itp."],
                  ] as const).map(([kind, label, accept, hint]) => {
                    const files =
                      kind === "map" ? mapUploads : kind === "licencja" ? licenceUploads : otherUploads;
                    const setFiles =
                      kind === "map" ? setMapUploads : kind === "licencja" ? setLicenceUploads : setOtherUploads;
                    return (
                      <div key={kind} className="space-y-1">
                        <div className="text-xs">
                          <span className="font-semibold">{label}</span>{" "}
                          <span className="text-[var(--color-text-muted)]">— {hint}</span>
                        </div>
                        <FileDropZone files={files} onFilesChange={setFiles} multiple accept={accept} />
                        {files.length > 0 && (
                          <Button
                            size="sm"
                            loading={m.uploadResultFiles.isPending}
                            onClick={() =>
                              runAction(() =>
                                m.uploadResultFiles.mutate(
                                  { id: order.id, files, kind },
                                  { onSuccess: () => setFiles([]), onError: onActionError },
                                ),
                              )
                            }
                          >
                            <Upload size={13} className="mr-1" /> Wgraj ({files.length})
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {order.result_files.length === 0 ? (
                <EmptyState>
                  Brak plików — wgraj mapę (DXF / DWG / GML / ZIP) i licencję (PDF / XML / ODT).
                  Licencja podepnie się potem sama do wniosków urzędowych.
                </EmptyState>
              ) : (
                <div className="space-y-1">
                  {order.result_files.map((rf) => (
                    <div key={rf.id} className="flex items-center gap-2 text-xs">
                      <a
                        href={rf.file.url ?? "#"}
                        download={rf.file.original_filename}
                        className="font-mono hover:underline truncate"
                      >
                        {rf.file.original_filename}
                      </a>
                      <span className="text-[10px] font-mono uppercase tracking-wide text-[var(--color-text-muted)]">
                        {rf.kind === "licencja" ? "licencja" : rf.kind === "map" ? "mapa" : "inne"}
                      </span>
                      <span className="text-[var(--color-text-muted)]">{fmtDate(rf.created_at)}</span>
                      {/* Usuwać wolno też na zamówieniu zakończonym — skasowanie
                          OSTATNIEJ mapy/licencji cofa status do „Wysłano" (backend). */}
                      {canManage && (
                        <button
                          className="ml-auto text-[var(--color-text-muted)] hover:text-red-600"
                          title={
                            rf.kind === "other"
                              ? "Usuń plik"
                              : "Usuń plik — skasowanie ostatniej mapy/licencji cofnie status zamówienia do „Wysłano do starostwa”"
                          }
                          onClick={() =>
                            runAction(() =>
                              m.deleteResultFile.mutate(
                                { id: order.id, resultFileId: rf.id },
                                { onError: onActionError },
                              ),
                            )
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </SectionCard>

          {/* ── uwagi (treść wniosku, sekcja 11) ── */}
          <SectionCard title="Uwagi">
            <div className="space-y-2">
              <div className="text-[11px] text-[var(--color-text-muted)]">
                Widoczne na wniosku — dopisane do sekcji 11.
              </div>
              {editingNotes ? (
                <div className="space-y-1">
                  <textarea
                    className="input-base h-14 w-full"
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                  />
                  {order.generated_at && (
                    <div className="text-[11px] text-[var(--color-text-muted)]">
                      Wniosek jest już wygenerowany — wygeneruj go ponownie, żeby zmiana
                      trafiła na PDF.
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      loading={m.update.isPending}
                      onClick={() =>
                        runAction(() =>
                          m.update.mutate(
                            { id: order.id, payload: { notes: notesDraft } },
                            { onSuccess: () => setEditingNotes(false), onError: onActionError },
                          ),
                        )
                      }
                    >
                      Zapisz
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditingNotes(false)}>
                      Anuluj
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {order.notes ? (
                    <div className="text-xs whitespace-pre-wrap">{order.notes}</div>
                  ) : (
                    <EmptyState>Brak uwag</EmptyState>
                  )}
                  {canManage && !CLOSED.includes(order.status) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setNotesDraft(order.notes);
                        setEditingNotes(true);
                      }}
                    >
                      <Pencil size={13} className="mr-1" /> Edytuj
                    </Button>
                  )}
                </>
              )}
            </div>
          </SectionCard>

          {/* ── komentarze ── */}
          <SectionCard title={`Komentarze (${order.comments.length})`}>
            <div className="space-y-3">
              {order.comments.length === 0 ? (
                <EmptyState>Brak komentarzy</EmptyState>
              ) : (
                <div className="space-y-2">
                  {order.comments.map((c) => {
                    const own = !!me && String(c.author) === String(me.id);
                    const editing = editingCommentId === c.id;
                    return (
                      <div key={c.id} className="text-xs">
                        <div className="flex items-center gap-2 text-[var(--color-text-muted)]">
                          <span>
                            {c.author_name || "—"} · {fmtDateTime(c.created_at)}
                            {c.is_edited && " · edytowany"}
                          </span>
                          {own && !editing && (
                            <span className="ml-auto flex gap-2">
                              <button
                                title="Edytuj komentarz"
                                className="hover:text-[var(--color-text-primary)]"
                                onClick={() => {
                                  setEditCommentText(c.body);
                                  setEditingCommentId(c.id);
                                }}
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                title="Usuń komentarz"
                                className="hover:text-red-600"
                                onClick={() =>
                                  runAction(() =>
                                    m.deleteComment.mutate(
                                      { id: order.id, commentId: c.id },
                                      { onError: onActionError },
                                    ),
                                  )
                                }
                              >
                                <Trash2 size={13} />
                              </button>
                            </span>
                          )}
                        </div>
                        {editing ? (
                          <div className="space-y-1 mt-1">
                            <RichTextEditor
                              value={editCommentText}
                              onChange={setEditCommentText}
                              disabled={m.updateComment.isPending}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                disabled={!editCommentText.trim()}
                                loading={m.updateComment.isPending}
                                onClick={() =>
                                  runAction(() =>
                                    m.updateComment.mutate(
                                      { id: order.id, commentId: c.id, body: editCommentText },
                                      {
                                        onSuccess: () => setEditingCommentId(null),
                                        onError: onActionError,
                                      },
                                    ),
                                  )
                                }
                              >
                                Zapisz
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => setEditingCommentId(null)}
                              >
                                Anuluj
                              </Button>
                            </div>
                          </div>
                        ) : isHtmlContent(c.body) ? (
                          <div
                            className="rich-text-content"
                            dangerouslySetInnerHTML={{ __html: sanitize(c.body) }}
                          />
                        ) : (
                          <div className="whitespace-pre-wrap">{c.body}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="space-y-1">
                <RichTextEditor
                  value={commentText}
                  onChange={setCommentText}
                  disabled={m.addComment.isPending}
                  placeholder="Treść komentarza…"
                />
                <Button
                  size="sm"
                  disabled={!commentText.trim()}
                  loading={m.addComment.isPending}
                  onClick={() =>
                    runAction(() =>
                      m.addComment.mutate(
                        { id: order.id, body: commentText },
                        { onSuccess: () => setCommentText(""), onError: onActionError },
                      ),
                    )
                  }
                >
                  <MessageSquarePlus size={13} className="mr-1" /> Dodaj komentarz
                </Button>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      {/* ── historia (pełna szerokość — wpisy bywają długie) ── */}
      <OrderHistorySection events={order.history ?? []} />

      {feeModal && <MzRequestFeeModal order={order} onClose={() => setFeeModal(false)} />}
      {editDooModal && (
        <EditDooModal
          module="mz"
          orderNumber={order.number}
          currentFee={
            order.fee_id
              ? { id: order.fee_id, amount: order.fee_amount, status: order.fee_status }
              : null
          }
          currentDooFilename={order.doo_file?.original_filename}
          pending={m.editDoo.isPending}
          onClose={() => setEditDooModal(false)}
          onSubmit={(payload) =>
            runAction(() =>
              m.editDoo.mutate(
                { id: order.id, payload },
                { onSuccess: () => setEditDooModal(false), onError: onActionError },
              ),
            )
          }
          onRequestNew={
            ["new", "sent"].includes(order.status)
              ? () => { setEditDooModal(false); setFeeModal(true); }
              : undefined
          }
        />
      )}
      {poaFeeModal && (
        <MzPoaFeeModal
          order={order}
          pending={m.setPoaFee.isPending}
          onClose={() => setPoaFeeModal(false)}
          onSubmit={(payload) =>
            runAction(() =>
              m.setPoaFee.mutate(
                { id: order.id, payload },
                { onSuccess: () => setPoaFeeModal(false), onError: onActionError },
              ),
            )
          }
        />
      )}
    </div>
  );
}
